import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Plus, UserCircle, Clock, CheckCircle, XCircle, Pencil,
  Trash, CurrencyDollar, ListChecks, CaretDown, X, Printer
} from '@phosphor-icons/react';
import { AttendanceTab, SalarySuggestModal } from '@/components/staff/AttendanceTab';

const EMPTY_STAFF = { name: '', role: 'Maid', phone: '', salary: 0, notes: '', expected_arrival: '', weekly_off: 'sunday', paid_leaves_per_month: 2 };

export default function StaffPage() {
  const { api, user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [tab, setTab] = useState('staff');
  const [form, setForm] = useState(EMPTY_STAFF);
  const [salaryStaff, setSalaryStaff] = useState(null);
  const [taskForm, setTaskForm] = useState({ title: '', description: '', assigned_to: '', priority: 'medium', due_date: '' });
  const [loading, setLoading] = useState(true);

  const isAdmin = user?.role === 'admin';

  const fetchData = async () => {
    try {
      const [staffRes, tasksRes] = await Promise.all([
        api.get('/staff'),
        api.get('/staff/tasks/all')
      ]);
      setStaff(staffRes.data);
      setTasks(tasksRes.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [api]);

  const handleSaveStaff = async (e) => {
    e.preventDefault();
    try {
      if (editingStaff) {
        await api.put(`/staff/${editingStaff.staff_id}`, form);
      } else {
        await api.post('/staff', form);
      }
      setShowForm(false);
      setEditingStaff(null);
      setForm(EMPTY_STAFF);
      fetchData();
    } catch (err) { console.error(err); }
  };

  const handleCheckIn = async (staffId) => {
    try {
      await api.post(`/staff/${staffId}/check-in`);
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const handleCheckOut = async (staffId) => {
    try {
      await api.post(`/staff/${staffId}/check-out`);
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const handleDeleteStaff = async (staffId) => {
    if (!window.confirm('Delete this staff member?')) return;
    await api.delete(`/staff/${staffId}`);
    fetchData();
  };

  const handleSaveTask = async (e) => {
    e.preventDefault();
    try {
      await api.post('/staff/tasks', taskForm);
      setShowTaskForm(false);
      setTaskForm({ title: '', description: '', assigned_to: '', priority: 'medium', due_date: '' });
      fetchData();
    } catch (err) { console.error(err); }
  };

  const handleTaskStatus = async (taskId, status) => {
    await api.put(`/staff/tasks/${taskId}`, { status });
    fetchData();
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="staff-page" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="overline">Staff Management</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Your Team</h1>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <>
              <button onClick={() => { setShowTaskForm(true); }} data-testid="add-task-btn" className="px-4 py-2 rounded-full border border-border text-sm font-medium hover:bg-muted transition-colors flex items-center gap-2">
                <ListChecks size={18} /> Add Task
              </button>
              <button onClick={() => { setShowForm(true); setEditingStaff(null); setForm(EMPTY_STAFF); }} data-testid="add-staff-btn" className="px-4 py-2 rounded-full bg-[#FF6B35] text-white btn-glow text-sm font-semibold flex items-center gap-2">
                <Plus size={18} /> Add Staff
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-muted/50 rounded-xl p-1 w-fit">
        {['staff', 'attendance', 'tasks'].map(t => (
          <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
            className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${tab === t ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
            {t} {t === 'tasks' && tasks.filter(x => x.status === 'pending').length > 0 && <span className="ml-1 text-xs bg-accent text-accent-foreground px-1.5 py-0.5 rounded-full">{tasks.filter(x => x.status === 'pending').length}</span>}
          </button>
        ))}
      </div>

      {/* Staff List */}
      {tab === 'staff' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {staff.map(s => (
            <div key={s.staff_id} className="glass-card p-5 card-hover" data-testid={`staff-card-${s.staff_id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                    {s.name?.[0]?.toUpperCase()}
                  </div>
                  <div>
                    <p className="font-semibold text-sm">{s.name}</p>
                    <p className="text-xs text-muted-foreground">{s.role}</p>
                  </div>
                </div>
                {isAdmin && (
                  <div className="flex gap-1">
                    <button onClick={() => window.open(`/staff-statement/${s.staff_id}`, '_blank')} data-testid={`statement-btn-${s.staff_id}`} title="Monthly statement" className="p-1.5 rounded-lg hover:bg-muted text-blue-500"><Printer size={14} /></button>
                    <button onClick={() => setSalaryStaff(s)} data-testid={`salary-btn-${s.staff_id}`} title="Salary" className="p-1.5 rounded-lg hover:bg-muted text-[#22C55E]"><CurrencyDollar size={14} /></button>
                    <button onClick={() => { setEditingStaff(s); setForm({ name: s.name, role: s.role, phone: s.phone, salary: s.salary, notes: s.notes, expected_arrival: s.expected_arrival || '', weekly_off: s.weekly_off || 'sunday', paid_leaves_per_month: s.paid_leaves_per_month ?? 2 }); setShowForm(true); }} className="p-1.5 rounded-lg hover:bg-muted"><Pencil size={14} /></button>
                    <button onClick={() => handleDeleteStaff(s.staff_id)} className="p-1.5 rounded-lg hover:bg-destructive/10 text-destructive"><Trash size={14} /></button>
                  </div>
                )}
              </div>
              {s.phone && <p className="text-xs text-muted-foreground mb-2">Phone: {s.phone}</p>}
              <p className="text-xs text-muted-foreground mb-1">Salary: Rs.{s.salary?.toLocaleString('en-IN')}/month</p>
              {s.expected_arrival && <p className="text-xs text-muted-foreground mb-3 flex items-center gap-1"><Clock size={12} /> Expected by {s.expected_arrival} · off: {s.weekly_off || 'sunday'}</p>}
              <div className="flex gap-2">
                <button onClick={() => handleCheckIn(s.staff_id)} data-testid={`checkin-${s.staff_id}`} className="flex-1 py-2 rounded-lg text-xs font-medium bg-green-500/10 text-green-600 hover:bg-green-500/20 transition-colors flex items-center justify-center gap-1">
                  <CheckCircle size={14} /> Check In
                </button>
                <button onClick={() => handleCheckOut(s.staff_id)} data-testid={`checkout-${s.staff_id}`} className="flex-1 py-2 rounded-lg text-xs font-medium bg-red-500/10 text-red-500 hover:bg-red-500/20 transition-colors flex items-center justify-center gap-1">
                  <XCircle size={14} /> Check Out
                </button>
              </div>
            </div>
          ))}
          {staff.length === 0 && (
            <div className="col-span-full text-center py-16 text-muted-foreground">
              <UserCircle size={48} className="mx-auto mb-3 opacity-50" />
              <p>No staff members added yet</p>
              {isAdmin && <p className="text-sm mt-1">Click "Add Staff" to get started</p>}
            </div>
          )}
        </div>
      )}

      {/* Attendance Register */}
      {tab === 'attendance' && <AttendanceTab api={api} isAdmin={isAdmin} />}

      {/* Tasks List */}
      {tab === 'tasks' && (
        <div className="space-y-3">
          {tasks.map(t => {
            const assignee = staff.find(s => s.staff_id === t.assigned_to);
            return (
              <div key={t.task_id} className={`rounded-xl border border-border bg-card p-4 flex items-center justify-between gap-4 ${t.status === 'completed' ? 'opacity-60' : ''}`} data-testid={`task-${t.task_id}`}>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <button
                    onClick={() => handleTaskStatus(t.task_id, t.status === 'completed' ? 'pending' : 'completed')}
                    className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors
                      ${t.status === 'completed' ? 'bg-primary border-primary text-primary-foreground' : 'border-border hover:border-primary'}`}
                  >
                    {t.status === 'completed' && <CheckCircle size={12} weight="bold" />}
                  </button>
                  <div className="min-w-0">
                    <p className={`text-sm font-medium ${t.status === 'completed' ? 'line-through' : ''}`}>{t.title}</p>
                    <p className="text-xs text-muted-foreground">{assignee?.name || 'Unassigned'} {t.due_date ? `- Due: ${t.due_date}` : ''}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium
                    ${t.priority === 'high' ? 'bg-red-500/10 text-red-500' : t.priority === 'medium' ? 'bg-yellow-500/10 text-yellow-600' : 'bg-muted text-muted-foreground'}`}>
                    {t.priority}
                  </span>
                  <button onClick={() => api.delete(`/staff/tasks/${t.task_id}`).then(fetchData)} className="p-1 text-muted-foreground hover:text-destructive"><Trash size={14} /></button>
                </div>
              </div>
            );
          })}
          {tasks.length === 0 && <p className="text-center text-muted-foreground py-12">No tasks assigned yet</p>}
        </div>
      )}

      {/* Add/Edit Staff Modal */}
      {showForm && (
        <Modal title={editingStaff ? 'Edit Staff' : 'Add Staff'} onClose={() => { setShowForm(false); setEditingStaff(null); }}>
          <form onSubmit={handleSaveStaff} className="space-y-4">
            <Field label="Name" value={form.name} onChange={v => setForm({...form, name: v})} required testId="staff-name-input" />
            <div>
              <label className="overline block mb-2">Role</label>
              <select value={form.role} onChange={e => setForm({...form, role: e.target.value})} data-testid="staff-role-select" className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none">
                {['Maid', 'Cook', 'Driver', 'Gardener', 'Security', 'Nanny', 'Other'].map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
            <Field label="Phone" value={form.phone} onChange={v => setForm({...form, phone: v})} testId="staff-phone-input" />
            <Field label="Monthly Salary (Rs.)" type="number" value={form.salary} onChange={v => setForm({...form, salary: Number(v)})} testId="staff-salary-input" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Expected Arrival" type="time" value={form.expected_arrival} onChange={v => setForm({...form, expected_arrival: v})} testId="staff-arrival-input" />
              <div>
                <label className="overline block mb-2">Weekly Off</label>
                <select value={form.weekly_off} onChange={e => setForm({...form, weekly_off: e.target.value})} data-testid="staff-weeklyoff-select" className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none capitalize">
                  {['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'none'].map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            </div>
            <Field label="Paid Leaves / Month" type="number" value={form.paid_leaves_per_month} onChange={v => setForm({...form, paid_leaves_per_month: Number(v)})} testId="staff-leaves-input" />
            <Field label="Notes" value={form.notes} onChange={v => setForm({...form, notes: v})} testId="staff-notes-input" />
            <button type="submit" data-testid="save-staff-btn" className="w-full py-3 rounded-full bg-[#FF6B35] text-white btn-glow font-semibold text-sm">
              {editingStaff ? 'Update Staff' : 'Add Staff'}
            </button>
          </form>
        </Modal>
      )}

      {/* Add Task Modal */}
      {showTaskForm && (
        <Modal title="Add Task" onClose={() => setShowTaskForm(false)}>
          <form onSubmit={handleSaveTask} className="space-y-4">
            <Field label="Task Title" value={taskForm.title} onChange={v => setTaskForm({...taskForm, title: v})} required testId="task-title-input" />
            <Field label="Description" value={taskForm.description} onChange={v => setTaskForm({...taskForm, description: v})} testId="task-desc-input" />
            <div>
              <label className="overline block mb-2">Assign To</label>
              <select value={taskForm.assigned_to} onChange={e => setTaskForm({...taskForm, assigned_to: e.target.value})} data-testid="task-assign-select" className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none" required>
                <option value="">Select staff...</option>
                {staff.map(s => <option key={s.staff_id} value={s.staff_id}>{s.name} ({s.role})</option>)}
              </select>
            </div>
            <div>
              <label className="overline block mb-2">Priority</label>
              <select value={taskForm.priority} onChange={e => setTaskForm({...taskForm, priority: e.target.value})} data-testid="task-priority-select" className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none">
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            <Field label="Due Date" type="date" value={taskForm.due_date} onChange={v => setTaskForm({...taskForm, due_date: v})} testId="task-date-input" />
            <button type="submit" data-testid="save-task-btn" className="w-full py-3 rounded-full bg-[#FF6B35] text-white btn-glow font-semibold text-sm">Add Task</button>
          </form>
        </Modal>
      )}
      {/* Salary Suggest Modal */}
      {salaryStaff && <SalarySuggestModal api={api} staff={salaryStaff} onClose={() => setSalaryStaff(null)} />}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-heading text-xl font-bold">{title}</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-muted"><X size={20} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', required, testId }) {
  return (
    <div>
      <label className="overline block mb-2">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} data-testid={testId} required={required}
        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-[#FF6B35]/50 focus:ring-1 focus:ring-[#FF6B35]/30 focus:bg-background outline-none transition-all text-sm" />
    </div>
  );
}
